import { catalogCategoryList } from '@/features/catalog/data';
import type { CatalogGalleryImage } from '@/features/catalog/types';
import type { AdminCatalogItemDetail, CatalogItemPreviewImage } from '@/features/content/types';
import { buildMockStorageKey, mockS3Prefixes } from '@/features/mock/storage';

const getFileExtension = (value: string) => {
  const normalizedValue = value.split('?')[0] ?? value;
  const lastDotIndex = normalizedValue.lastIndexOf('.');

  if (lastDotIndex < 0) {
    return 'jpg';
  }

  return normalizedValue.slice(lastDotIndex + 1).toLowerCase();
};

const getContentType = (value: string) => {
  const extension = getFileExtension(value);

  switch (extension) {
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'gif':
      return 'image/gif';
    case 'svg':
      return 'image/svg+xml';
    case 'jpg':
    case 'jpeg':
    default:
      return 'image/jpeg';
  }
};

const createPublishedAt = (categoryIndex: number, itemIndex: number) => {
  const day = String(Math.max(1, 28 - categoryIndex * 7 - itemIndex)).padStart(2, '0');
  return `2026-04-${day}T09:00:00`;
};

const getGalleryImages = (
  gallery: CatalogGalleryImage[],
  fallbackImageUrl: string | undefined,
): CatalogItemPreviewImage[] => {
  const imageUrls = gallery
    .map((image) => image.src)
    .filter((imageUrl): imageUrl is string => Boolean(imageUrl));

  const sourceUrls = imageUrls.length > 0 ? imageUrls : fallbackImageUrl ? [fallbackImageUrl] : [];

  return sourceUrls.map((imageUrl, index) => {
    const fileExtension = getFileExtension(imageUrl);

    return {
      id: index + 1,
      fileName: buildMockStorageKey(
        mockS3Prefixes.catalogPreviewImages,
        `catalog-preview-${String(index + 1)}.${fileExtension}`,
      ),
      imageUrl,
      contentType: getContentType(imageUrl),
      fileSize: 0,
      displayOrder: index + 1,
    };
  });
};

export const createInitialMockCatalogItems = (): AdminCatalogItemDetail[] => {
  let nextId = 1;

  return catalogCategoryList.flatMap((category, categoryIndex) => {
    return category.cards.map((card, itemIndex) => {
      const detailImageUrl = card.imageSrc ?? card.gallery[0]?.src ?? '';
      const detailFileExtension = getFileExtension(detailImageUrl);
      const previewImages = getGalleryImages(card.gallery, card.imageSrc);

      const item: AdminCatalogItemDetail = {
        id: nextId,
        category: card.categoryId,
        title: card.title,
        slug: card.slug,
        summary: card.summary,
        detailImageFileName: buildMockStorageKey(
          mockS3Prefixes.catalogDetailImages,
          `${card.slug}-detail.${detailFileExtension}`,
        ),
        detailImageUrl,
        detailImageContentType: getContentType(detailImageUrl),
        detailImageFileSize: 0,
        displayOrder: itemIndex + 1,
        isPublished: true,
        publishedAt: createPublishedAt(categoryIndex, itemIndex),
        mainPreviewImageIndex: 0,
        previewImages,
      };

      nextId += 1;
      return item;
    });
  });
};
